// ---------------------------------------------------------------------------
// showcaseWindow — a janela do DESTAQUE de um tema na vitrine.
//
// Blueprint: `docs/systems/admin-themes.md`.
//
// ## Por que o destaque precisa de prazo
//
// Destaque sem quem o desative é o **BUG-39**: campanha vencida servida em
// silêncio. Um tema fixado "para sempre" depende de alguém lembrar de
// desmarcá-lo, e a memória do operador é justamente o que este painel existe
// para não exigir.
//
// ## Precisão de HORA, e não de dia
//
// Diferente da temporada (`themeSeasons`), que é campanha de meses e por isso
// compara chave de DIA, o destaque é alavanca operacional: "põe este tema na
// frente agora, até domingo à noite". Prazo em dia não expressa isso.
//
// ## 🔴 O instante é de BRASÍLIA, nunca do aparelho
//
// O operador digita "20:00" pensando no horário do produto. Interpretar isso no
// fuso do NAVEGADOR faria o destaque começar em hora diferente conforme onde
// quem cadastrou estivesse — e o mesmo cadastro produziria janelas diferentes.
//
// O offset é fixo em `-03:00`, que é a convenção já usada em `themeSeasons` e
// em `bienalWindow`. O Brasil não tem horário de verão desde 2019; se ele
// voltar, estes dois helpers são o lugar de tratar.
// ---------------------------------------------------------------------------

/** Offset do fuso do produto. Ver a nota sobre horário de verão no cabeçalho. */
const OFFSET_BR = "-03:00";

/**
 * `"2026-10-01T20:00"` (valor de um `<input type="datetime-local">`) → ms.
 *
 * Devolve `null` para entrada vazia ou malformada — inventar uma queda aqui
 * gravaria uma janela que o operador não pediu.
 */
export function brLocalToMs(valor: string | null | undefined): number | null {
  const v = (valor ?? "").trim();
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v)) return null;
  const ms = Date.parse(`${v}:00${OFFSET_BR}`);
  return Number.isFinite(ms) ? ms : null;
}

/** ms → `"2026-10-01T20:00"` no fuso do produto, para preencher o input. */
export function msToBrLocal(ms: number | null | undefined): string {
  if (typeof ms !== "number" || !Number.isFinite(ms)) return "";
  // `sv-SE` devolve "AAAA-MM-DD HH:MM:SS"; o input quer "T" no lugar do espaço.
  const partes = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(ms));
  return partes.replace(" ", "T");
}

export interface ShowcaseWindow {
  /** Começa a valer. Ausente = já vale. */
  from?: number | null;
  /** Deixa de valer. Ausente = não expira. */
  until?: number | null;
}

/**
 * O destaque está valendo AGORA?
 *
 * Cada ponta é independente e opcional: só `from` é "a partir de", só `until` é
 * "até", os dois é a janela, nenhum é permanente.
 *
 * ⚠️ `from` é inclusivo e `until` é EXCLUSIVO. O operador digita o instante em
 * que o destaque deve acabar ("domingo 23:59"), e nesse instante ele já saiu.
 * Fazer `until` inclusivo daria um minuto extra que ninguém pediu, e a diferença
 * seria invisível até alguém agendar dois destaques em sequência.
 */
export function isShowcaseLive(janela: ShowcaseWindow, agora: number = Date.now()): boolean {
  const {from, until} = janela;
  if (typeof from === "number" && agora < from) return false;
  if (typeof until === "number" && agora >= until) return false;
  return true;
}

/** Por que o destaque não está valendo — para a tela dizer, em vez de só sumir. */
export type ShowcaseState = "vivo" | "agendado" | "expirado" | "desligado";

export function showcaseState(
  pinned: boolean | undefined,
  janela: ShowcaseWindow,
  agora: number = Date.now(),
): ShowcaseState {
  if (!pinned) return "desligado";
  if (typeof janela.from === "number" && agora < janela.from) return "agendado";
  if (typeof janela.until === "number" && agora >= janela.until) return "expirado";
  return "vivo";
}

/**
 * A janela é coerente?
 *
 * Fim antes do começo é a única combinação que não significa nada: ela produz
 * um destaque que nunca acontece, e o painel diria "salvo" sem nada mudar — a
 * classe "toquei e nada aconteceu".
 */
export function isWindowSane(janela: ShowcaseWindow): boolean {
  const {from, until} = janela;
  if (typeof from !== "number" || typeof until !== "number") return true;
  return until > from;
}
